import { Injectable } from '@angular/core';
import { EChartsOption } from 'echarts';
import { Observable, Subject } from 'rxjs';
import { ChartConfig, ClimateChart, YAxis } from '../../../model/climate/climate-chart';
import { MAPTYCS_CHART_COLOR, COPERNICUS_RED_COLOR, getSSPColor } from './custom-theme';
import { ClimateChartHelper } from './util/climate-chart-helper';

@Injectable()
export class TxxChangeService {

  private readonly locationChartSubject: Subject<any>;
    private readonly addressChartSubject: Subject<any>;
  
    constructor() {
      this.locationChartSubject = new Subject<any>();
      this.addressChartSubject = new Subject<any>();
    }
  
    emitLocationChartEvent(chartInstance: any): void {
      this.locationChartSubject.next(chartInstance);
    }
  
    getLocationChartSubject(): Observable<any> {
      return this.locationChartSubject.asObservable();
    }
  
    emitAddressChartEvent(chartInstance: any): void {
      this.addressChartSubject.next(chartInstance);
    }
  
    getAddressChartSubject(): Observable<any> {
      return this.addressChartSubject.asObservable();
    }
  
    options: EChartsOption = {
      height: '80%',
      animation: false,
      animationDuration: 0,
      animationDurationUpdate: 0,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        valueFormatter: (value: any) => ClimateChartHelper.formatChartValue(value)
      },
      legend: {},
      toolbox: ClimateChartHelper.getToolboxConfig('Change in TXx', 'TXx_Change'),
      dataZoom: {
        show: false,
        start: 0,
        end: 100
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: ClimateChartHelper.getDualXAxisConfig({
        type: 'category',
        boundaryGap: ['10%', '10%'],
        axisLabel: { interval: 0 }
      }),
      yAxis: [
        {
          type: 'value',
          scale: true,
          name: 'Change in Maximum of daily maximum temperature (°C)',
          position: 'left',
          alignTicks: true,
          axisLine: {
            show: true,
          },
          axisLabel: {
            formatter: '{value}'
          },
          boundaryGap: [0.2, 0.2]
        }
      ],
      series: [],
      dataset: {}
    };
  
    updateTxxChangeChart(echartsInstance: any, climateChart: ClimateChart) {
      const chartConfig: ChartConfig = climateChart.chartConfig;
      const yAxis: YAxis = chartConfig.yAxis;
      const seriesData = [];
      const source = [];
  
      source.push(climateChart.xAxis);
  
      for (const [key, value] of Object.entries(climateChart.series)) {
        // Enable dual xAxis for Historical bar centering
      seriesData.push(ClimateChartHelper.getSeriesConfig(key, chartConfig.type, getSSPColor, true));
        // Use common method to pad SSP scenario data
        source.push(ClimateChartHelper.padSSPScenarioData(key, value, climateChart.xAxis));
      }
  
      // Calculate bounds using the utility method
      const bounds = ClimateChartHelper.calculateAxisBounds(
        climateChart.series, 
        yAxis.min, 
        yAxis.max, 
        yAxis.interval
      );
  
      echartsInstance.setOption({
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
        xAxis: ClimateChartHelper.getDualXAxisConfig({
          type: 'category',
          boundaryGap: ['10%', '10%'],
          axisLabel: { interval: 0 }
        }),
        yAxis: [
          {
            type: 'value',
            scale: true,
            name: yAxis.title || '°C-day',
            min: bounds.min,
            max: bounds.max,
            interval: bounds.interval,
            position: 'left',
            alignTicks: true,
            axisLine: {
              show: true,
            },
            axisLabel: {
              formatter: '{value} '
            },
            boundaryGap: [0.2, 0.2]
          }
        ],
        dataset: {
          source: source
        },
        series: seriesData
      });
      
      // Attach download handler to add title only when downloading
      ClimateChartHelper.attachDownloadHandler(echartsInstance, 'Heat Stress - Maximum of daily maximum temperature (TXx)', 'Heat_Stress_Maximum_of_daily_maximum_temperature_TXx_Change');
      if (echartsInstance && typeof echartsInstance.on === 'function') {
        let applying = false;
        try { echartsInstance.off && echartsInstance.off('finished'); } catch {}
        echartsInstance.on('finished', () => {
          if (applying) return;
          applying = true;
          echartsInstance.setOption({
            xAxis: ClimateChartHelper.getDualXAxisConfig({
              type: 'category',
              boundaryGap: ['10%', '10%'],
              axisLabel: { interval: 0 }
            })
          }, { replaceMerge: ['xAxis'] });
          setTimeout(() => { applying = false; }, 0);
        });
      }
    }
  // private locationChartSubject = new Subject<any>();
  // private addressChartSubject = new Subject<any>();

  // options: EChartsOption = {
  //   color: [MAPTYCS_CHART_COLOR],
  //   height: '80%',
  //   tooltip: {
  //     trigger: 'axis',
  //     axisPointer: {
  //       type: 'cross',
  //       label: {
  //         backgroundColor: '#6a7985'
  //       }
  //     },
  //     valueFormatter: (value: any) => typeof value === 'number' ? value.toFixed(3) : value
  //   },
  //   legend: { top: 10 },
  //   toolbox: ClimateChartHelper.getToolboxConfig(),
  //   grid: {
  //     left: '3%',
  //     right: '4%',
  //     bottom: '3%',
  //     containLabel: true
  //   },
  //   xAxis: {
  //     type: 'category',
  //     boundaryGap: false,
  //     data: [],
  //     name: 'Year Range',
  //     nameLocation: 'middle',
  //     nameGap: 30
  //   },
  //   yAxis: {
  //     type: 'value',
  //     name: 'Change in Maximum of daily maximum temperature (°C)',
  //     nameLocation: 'middle',
  //     nameGap: 50,
  //     min: 0,
  //     max: 10,
  //     interval: 1
  //   },
  //   series: []
  // };

  // constructor() {
  // }

  // emitLocationChartEvent(instance: any) {
  //   this.locationChartSubject.next(instance);
  // }

  // emitAddressChartEvent(instance: any) {
  //   this.addressChartSubject.next(instance);
  // }

  // getLocationChartSubject() {
  //   return this.locationChartSubject;
  // }

  // getAddressChartSubject() {
  //   return this.addressChartSubject;
  // }

  // updateTxxChangeChart(instance: any, climateChart: ClimateChart) {
  //   if (instance !== undefined) {
  //     const seriesData = [];
  //     const xAxisData = climateChart.xAxis.slice(1); // Skip first element 'Year Range'

  //     for (const [key, value] of Object.entries(climateChart.series)) {
  //       const numericValues = value.slice(1).map(v => parseFloat(v));
  //       seriesData.push({
  //         name: key,
  //         type: climateChart.chartConfig.type,
  //         smooth: true,
  //         data: numericValues,
  //         itemStyle: { color: getSSPColor(key) }
  //       });
  //     }

  //     // Calculate bounds using the utility method
  //     const bounds = ClimateChartHelper.calculateAxisBounds(
  //       climateChart.series, 
  //       climateChart.chartConfig.yAxis.min, 
  //       climateChart.chartConfig.yAxis.max, 
  //       climateChart.chartConfig.yAxis.interval
  //     );

  //     instance.setOption({
  //       legend: { data: climateChart.legends },
  //       xAxis: {
  //         type: 'category',
  //         data: xAxisData,
  //         name: climateChart.chartConfig.xAxis.title,
  //         nameLocation: 'middle',
  //         nameGap: 30,
  //         axisLabel: {
  //           formatter: (value: string) => {
  //             const match = value.match(/(\d{4})-(\d{4})/);
  //             if (match) {
  //               const start = parseInt(match[1]);
  //               const end = parseInt(match[2]);
  //               return Math.floor((start + end) / 2).toString();
  //             }
  //             return value;
  //           }
  //         }
  //       },
  //       yAxis: {
  //         type: 'value',
  //         name: climateChart.chartConfig.yAxis.title,
  //         min: bounds.min,
  //         max: bounds.max,
  //         interval: bounds.interval,
  //         nameLocation: 'middle',
  //         nameGap: 50
  //       },
  //       series: seriesData
  //     });
  //   }
  // }
}
